/**
 * /upload-lab — permanent forensic upload environment.
 *
 * Constitutional reference: PLATFORM_REBUILD_DOCTRINE.md §G (golden path).
 * Architecture reference:   PLATFORM_REBUILD_ARCHITECTURE.md §0, §8.
 *
 * Phase A scope:
 *   - Auth-gated (any logged-in user can reach it).
 *   - Zero role chrome — no nav, no footer, no PWA shell, no lesson
 *     list, no passport. The page mounts at the route root.
 *   - Mounts the CANONICAL basket engine (ClipBasketV2). NEVER mounts
 *     a parallel implementation. This is the upload-lab's most
 *     important contract: it tests the same engine production uses.
 *   - Wraps the basket in OperationalStateProvider so the lifecycle
 *     drawer can subscribe.
 *   - Toggles `?diag=1` to surface the LifecycleDrawer.
 *
 * Out of scope (deliberately):
 *   - UploadProtectedMode HOC — Phase D.
 *   - Healthcheck strip — Phase E.
 *   - Desktop-UA banner — Phase D.
 *   - Recovery-on-mount call into uploadRecovery.attemptResume — Phase B.
 *
 * The page intentionally renders MINIMAL chrome: build stamp, user
 * email, basket modal, and (if ?diag=1) the lifecycle drawer. That's
 * it. If something else appears here in the future, it's a doctrine
 * violation — the lab must stay deterministic.
 */

import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  OperationalStateProvider,
  useOperationalState,
} from "../context/OperationalStateContext";
import ClipBasketV2 from "../components/upload/ClipBasketV2";
import LifecycleDrawer from "../components/upload/LifecycleDrawer";

const BUILD_STAMP = "phaseA.may22.2026.r1-upload-lab";

function UploadLabInner() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { state, appendEvent } = useOperationalState();
  const [basketOpen, setBasketOpen] = useState(false);

  // Auth gate. Page is auth-gated but role-agnostic per spec.
  useEffect(() => {
    if (loading) return;
    if (!user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    appendEvent("picker_open", { route: "/upload-lab", at_mount: true });
    // We log a picker_open on mount so the audit trail captures the
    // session start even if the operator doesn't tap Add videos.
  }, [appendEvent]);

  const onClose = useCallback(() => setBasketOpen(false), []);

  if (loading) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-300 text-sm font-mono" data-testid="upload-lab-loading">
        loading…
      </main>
    );
  }
  if (!user) return null;

  return (
    <main
      data-testid="upload-lab"
      className="min-h-screen bg-slate-950 text-slate-100 px-4 py-5"
    >
      <header className="mb-4 flex flex-col gap-1">
        <p className="text-[10px] uppercase tracking-[0.18em] text-amber-300 font-extrabold">
          Upload Lab · canonical forensic surface
        </p>
        <h1 className="text-xl font-extrabold leading-tight" data-testid="upload-lab-title">
          Golden path verification
        </h1>
        <p
          data-testid="upload-lab-build-stamp"
          className="text-[10.5px] text-amber-200 font-mono mt-1"
        >
          build {BUILD_STAMP} · user {user.email || user.id} · trace {state.trace_id || "(awaiting)"}
        </p>
      </header>

      <section className="rounded-xl border border-slate-800 bg-slate-900/50 p-3 mb-4 text-[11.5px] text-slate-300 leading-relaxed">
        <p>
          This page mounts the canonical basket engine in isolation —
          no nav, no role redirects, no lesson list, no passport
          chrome. Use it to verify the golden path on real iPhone Safari
          end-to-end:
        </p>
        <ol className="list-decimal ml-5 mt-2 space-y-0.5 text-[11px]">
          <li>Tap <b>Open canonical basket</b> below</li>
          <li>Pick an intent → tap <b>Add videos from Photos</b></li>
          <li>Select 1–3 clips → watch them stage</li>
          <li>Tap <b>Start uploads</b></li>
          <li>Verify each step in the lifecycle drawer (add <code>?diag=1</code> to URL)</li>
        </ol>
      </section>

      {!basketOpen ? (
        <button
          type="button"
          data-testid="upload-lab-open-basket"
          onClick={() => setBasketOpen(true)}
          className="w-full rounded-xl bg-amber-400 hover:bg-amber-300 text-slate-950 font-extrabold py-3"
        >
          Open canonical basket
        </button>
      ) : (
        <ClipBasketV2
          user={user}
          onClose={onClose}
          onHandedOff={(basketId) => {
            // Per spec §16 #10: when the basket handoff fires, log
            // basket_processed for the audit trail. We do NOT redirect.
            appendEvent("basket_processed", { basket_id: basketId, route: "/upload-lab" });
            // Modal closes itself via onClose.
          }}
        />
      )}

      <LifecycleDrawer />
    </main>
  );
}

export default function UploadLab() {
  return (
    <OperationalStateProvider>
      <UploadLabInner />
    </OperationalStateProvider>
  );
}
